import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CATALOG_STALE_BASE_MS, staleTimeWithJitterFromKey } from "@/lib/query-cache";
import { queryKeys } from "@/lib/query-keys";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogHeader,
  DialogScrollContent,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Edit3, Trash2, Mail, X } from "lucide-react";
import { getAdminSettings } from "@/integrations/backend/admin-settings";
import { useToast } from "@/hooks/use-toast";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { listEmailTemplates, createEmailTemplate, updateEmailTemplate, deleteEmailTemplate, type EmailTemplate as EmailTemplateRow } from "@/integrations/backend/email-templates";

const DEFAULT_SENDER_DOMAIN = "healthfq.com";

type EmailTemplate = EmailTemplateRow;

const emailTemplatesQueryKey = queryKeys.emailTemplates.all;
const adminSettingsQueryKey = queryKeys.admin.settings;

export function NotificationsView() {
  const queryClient = useQueryClient();
  const templatesQuery = useQuery({
    queryKey: emailTemplatesQueryKey,
    queryFn: listEmailTemplates,
    staleTime: staleTimeWithJitterFromKey(CATALOG_STALE_BASE_MS, [...emailTemplatesQueryKey]),
  });
  const settingsQuery = useQuery({
    queryKey: adminSettingsQueryKey,
    queryFn: getAdminSettings,
    staleTime: staleTimeWithJitterFromKey(CATALOG_STALE_BASE_MS, [...adminSettingsQueryKey]),
  });
  const templates = templatesQuery.data ?? [];
  const loading = templatesQuery.isLoading || settingsQuery.isLoading;

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("setup");
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const { toast } = useToast();

  // Form fields
  const [templateName, setTemplateName] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [senderName, setSenderName] = useState("");
  const [senderEmailLocal, setSenderEmailLocal] = useState("");
  const [mailMergeTerms, setMailMergeTerms] = useState<string[]>([]);
  const [newTerm, setNewTerm] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [defaultSettings, setDefaultSettings] = useState<{
    default_sender_name: string;
    default_sender_email: string;
  } | null>(null);

  // Step 2 - Rich text editor
  const editor = useEditor({
    extensions: [StarterKit],
    content: "",
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none min-h-[300px] p-4 focus:outline-none border rounded-md bg-white text-black",
      },
    },
  });

  useEffect(() => {
    const data = settingsQuery.data;
    if (data) {
      setDefaultSettings({
        default_sender_name: data.default_sender_name,
        default_sender_email: data.default_sender_email,
      });
    }
  }, [settingsQuery.data]);

  useEffect(() => {
    if (templatesQuery.isError && templatesQuery.error) {
      toast({
        title: "Error",
        description: (templatesQuery.error as Error).message,
        variant: "destructive",
      });
    }
  }, [templatesQuery.isError, templatesQuery.error, toast]);

  const openCreate = () => {
    setEditingTemplate(null);
    resetForm();
    if (defaultSettings) {
      setSenderName(defaultSettings.default_sender_name);
      const [local] = defaultSettings.default_sender_email.split("@");
      setSenderEmailLocal(local || "");
    }
    setIsDialogOpen(true);
    setActiveTab("setup");
  };

  const openEdit = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setTemplateName(template.name);
    setTemplateId(template.template_id);
    setSenderName(template.sender_name);
    const [local] = template.sender_email.split("@");
    setSenderEmailLocal(local || "");
    setMailMergeTerms(template.mail_merge_terms);
    setIsActive(template.is_active);
    editor?.commands.setContent(template.content);
    setIsDialogOpen(true);
    setActiveTab("setup");
  };

  const resetForm = () => {
    setTemplateName("");
    setTemplateId("");
    setSenderName("");
    setSenderEmailLocal("");
    setMailMergeTerms([]);
    setNewTerm("");
    setIsActive(true);
    editor?.commands.setContent("");
  };

  const handleAddTerm = () => {
    if (newTerm.trim() && !mailMergeTerms.includes(newTerm.trim())) {
      setMailMergeTerms([...mailMergeTerms, newTerm.trim()]);
      setNewTerm("");
    }
  };

  const handleRemoveTerm = (term: string) => {
    setMailMergeTerms(mailMergeTerms.filter((t) => t !== term));
  };

  const handleCreateOrUpdate = async () => {
    if (!templateName.trim() || !templateId.trim() || !senderName.trim() || !senderEmailLocal.trim()) {
      toast({
        title: "Required fields",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    const fullSenderEmail = `${senderEmailLocal}@${DEFAULT_SENDER_DOMAIN}`;

    try {
      const templateData = {
        template_id: templateId,
        name: templateName,
        sender_name: senderName,
        sender_email: fullSenderEmail,
        mail_merge_terms: mailMergeTerms,
        content: editor?.getHTML() || "",
        is_active: isActive,
      };

      if (editingTemplate) {
        await updateEmailTemplate(editingTemplate.id, templateData);
        toast({ title: "Success", description: "Email template updated successfully." });
      } else {
        const created = await createEmailTemplate(templateData as any);
        setEditingTemplate(created);
        toast({ title: "Template created", description: "Now add your email content." });
      }

      setActiveTab("content");
      await queryClient.invalidateQueries({ queryKey: emailTemplatesQueryKey });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleSaveContent = async () => {
    const content = editor?.getHTML() || "";

    if (!content.trim()) {
      toast({
        title: "Content required",
        description: "Please add content to the email template.",
        variant: "destructive",
      });
      return;
    }

    if (!editingTemplate) return;

    try {
      await updateEmailTemplate(editingTemplate.id, { content });
      toast({
        title: "Success",
        description: "Email content saved successfully.",
      });
      
      setIsDialogOpen(false);
      resetForm();
      setActiveTab("setup");
      await queryClient.invalidateQueries({ queryKey: emailTemplatesQueryKey });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this template?")) return;

    try {
      await deleteEmailTemplate(id);
      toast({
        title: "Success",
        description: "Email template deleted successfully.",
      });
      await queryClient.invalidateQueries({ queryKey: emailTemplatesQueryKey });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const insertMergeTerm = (term: string) => {
    editor?.commands.insertContent(`{{${term}}}`);
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Email Notifications</h1>
          <p className="text-gray-600">Manage email templates for notifications sent by the app.</p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="w-4 h-4 mr-2" />
          Create Template
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Email Templates</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Template ID</TableHead>
                  <TableHead>Sender</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[120px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                      No email templates yet. Create your first template to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  templates.map((template) => (
                    <TableRow key={template.id}>
                      <TableCell className="font-medium">{template.name}</TableCell>
                      <TableCell>
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                          {template.template_id}
                        </code>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-gray-400" />
                          <div className="text-sm">
                            <div className="font-medium">{template.sender_name}</div>
                            <div className="text-gray-500">{template.sender_email}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={template.is_active ? "success" : "secondary"}>
                          {template.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => openEdit(template)}>
                            <Edit3 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDelete(template.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogScrollContent className="max-w-3xl max-h-[90vh] bg-white text-black">
          <DialogHeader className="shrink-0">
            <DialogTitle>
              {editingTemplate ? "Edit" : "Create"} Email Template
            </DialogTitle>
            <DialogDescription>
              Configure your email template settings and content
            </DialogDescription>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col flex-1 min-h-0">
            <TabsList className="grid w-full grid-cols-2 shrink-0 bg-white text-black">
              <TabsTrigger value="setup">Setup</TabsTrigger>
              <TabsTrigger value="content">Content</TabsTrigger>
            </TabsList>

            <DialogBody>
            <TabsContent value="setup" className="space-y-4 py-4 mt-0">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="template-name">Template Name *</Label>
                  <Input
                    id="template-name"
                    placeholder="e.g., Welcome Email"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    className="bg-white text-black placeholder:text-gray-500"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="template-id">Template ID *</Label>
                  <Input
                    id="template-id"
                    placeholder="e.g., welcome_email"
                    value={templateId}
                    onChange={(e) => setTemplateId(e.target.value)}
                    disabled={!!editingTemplate}
                    className="bg-white text-black placeholder:text-gray-500 disabled:opacity-70"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sender-name">Sender Name *</Label>
                  <Input
                    id="sender-name"
                    placeholder="e.g., FitnessPro Team"
                    value={senderName}
                    onChange={(e) => setSenderName(e.target.value)}
                    className="bg-white text-black placeholder:text-gray-500"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sender-email">Sender Email *</Label>
                  <div className="flex gap-2">
                    <Input
                      id="sender-email"
                      placeholder="noreply"
                      value={senderEmailLocal}
                      onChange={(e) => setSenderEmailLocal(e.target.value)}
                      className="flex-1 bg-white text-black placeholder:text-gray-500"
                    />
                    <div className="flex items-center px-3 bg-white text-black rounded-md border">
                      @{DEFAULT_SENDER_DOMAIN}
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Mail Merge Terms</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add a merge term (e.g., first_name)"
                    value={newTerm}
                    onChange={(e) => setNewTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddTerm();
                      }
                    }}
                    className="bg-white text-black placeholder:text-gray-500"
                  />
                  <Button type="button" variant="outline" onClick={handleAddTerm} className="text-black border-gray-300 hover:bg-gray-100">
                    <Plus className="w-4 h-4 text-black" />
                  </Button>
                </div>
                {mailMergeTerms.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {mailMergeTerms.map((term) => (
                      <Badge key={term} variant="secondary" className="gap-1">
                        {term}
                        <button
                          type="button"
                          onClick={() => handleRemoveTerm(term)}
                          className="ml-1 hover:text-destructive"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-0.5">
                  <Label htmlFor="is-active">Active Status</Label>
                  <p className="text-sm text-gray-500">
                    Enable or disable this email template
                  </p>
                </div>
                <Switch
                  id="is-active"
                  checked={isActive}
                  onCheckedChange={setIsActive}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setIsDialogOpen(false)} className="text-black border-gray-300 hover:bg-gray-100">
                  Cancel
                </Button>
                <Button onClick={handleCreateOrUpdate}>
                  {editingTemplate ? "Save & Continue" : "Create & Continue"}
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="content" className="space-y-4 py-4 mt-0">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Email Content</Label>
                  {mailMergeTerms.length > 0 && (
                    <div className="flex gap-2 flex-wrap">
                      <span className="text-xs text-gray-500">Insert merge terms:</span>
                      {mailMergeTerms.map((term) => (
                        <Button
                          key={term}
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => insertMergeTerm(term)}
                          className="text-xs h-7"
                        >
                          {term}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-50 border-b px-4 py-2 flex gap-2 text-black">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => editor?.chain().focus().toggleBold().run()}
                      className={(editor?.isActive("bold") ? "bg-gray-200 " : "") + "text-black"}
                    >
                      <strong>B</strong>
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => editor?.chain().focus().toggleItalic().run()}
                      className={(editor?.isActive("italic") ? "bg-gray-200 " : "") + "text-black"}
                    >
                      <em>I</em>
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
                      className={(editor?.isActive("heading") ? "bg-gray-200 " : "") + "text-black"}
                    >
                      H
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => editor?.chain().focus().toggleBulletList().run()}
                      className={(editor?.isActive("bulletList") ? "bg-gray-200 " : "") + "text-black"}
                    >
                      • List
                    </Button>
                  </div>
                  <EditorContent editor={editor} />
                </div>
                <p className="text-xs text-gray-500">
                  Use the merge terms above to personalize your email. They will appear as {"{{term_name}}"} in the content.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setIsDialogOpen(false)} className="text-black border-gray-300 hover:bg-gray-100">
                  Cancel
                </Button>
                <Button onClick={handleSaveContent}>
                  Save Content
                </Button>
              </div>
            </TabsContent>
            </DialogBody>
          </Tabs>
        </DialogScrollContent>
      </Dialog>
    </div>
  );
}
